import ConsultationForm from "./ConsultationForm";
import useReveal from "@/hooks/useReveal";

const heroImage =
  "https://images.unsplash.com/photo-1557236751-b60abca1479e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxjb250ZW1wbGF0aXZlJTIwcGFyZW50JTIwY2hpbGQlMjBoYW5kc3xlbnwwfHx8fDE3ODEyMDYwODZ8MA&ixlib=rb-4.1.0&q=85";

export default function Hero({
  headline,
  subhead,
  pullquoteLabel = "Our Promise",
  pullquote,
  eyebrow = "Family Law Attorney · Orange County, California",
  primaryCtaLabel = "Schedule Your Confidential Consultation",
  ctaSubNote = "Confidential · No obligation",
  statBar,
  heroImage: heroImageProp,
  imageCaption,
  source,
  formHeadline,
  formSub,
  formEyebrow,
  formCtaLabel,
  showPriorResultsDisclaimer = true,
  compact = false,
  statHeading = "A New Law Firm Was Born After…",
  statContext = "A firsthand understanding of what families endure during the legal process inspired the creation of JOS Family Law.",
} = {}) {
  useReveal();

  const defaultHeadline = (
    <>
      You&rsquo;re not just{" "}
      <span className="italic font-medium text-ink">fighting a case.</span>
      <br className="hidden md:block" />
      You&rsquo;re fighting{" "}
      <span className="italic font-medium text-teal">for your child.</span>
    </>
  );

  const defaultSubhead = (
    <>
      Jos Family Law was founded by{" "}
      <span className="text-ink font-medium">Binoye Jos, Esq.</span> — a father
      who spent <span className="text-ink font-medium">$400,000</span> with{" "}
      <span className="text-ink font-medium">two prominent law firms</span> who
      told him he&rsquo;d never get equal custody. He fired them, represented
      himself, and won{" "}
      <span className="text-ink font-medium">equal custody</span> of his own
      son. He then went to law school and built this firm so you don&rsquo;t
      have to go through what he did.
    </>
  );

  const defaultPullquote = (
    <>
      We&rsquo;ve sat in your chair. We&rsquo;ve felt the weight of every
      filing.{" "}
      <span className="italic text-teal">
        We don&rsquo;t guess what&rsquo;s at stake — we know.
      </span>
    </>
  );

  return (
    <section
      data-testid="hero-section"
      className="relative bg-paper overflow-hidden pt-12 md:pt-16 lg:pt-20 pb-16 md:pb-24"
    >
      {/* Decorative side rule */}

      <div className="max-w-[1320px] mx-auto px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* LEFT — copy column */}
          <div className="lg:col-span-7 reveal">
            <div className="flex items-center gap-4 mb-8">
              <span
                aria-hidden
                className="inline-block w-10 h-px bg-teal"
              />
              <p
                data-testid="hero-eyebrow"
                className="font-mono text-[11px] tracking-[0.28em] uppercase text-gray-600"
              >
                {eyebrow}
              </p>
            </div>

            <h1
              data-testid="hero-headline"
              className={
                compact
                  ? "font-display text-[36px] leading-[1.05] md:text-[52px] lg:text-[60px] lg:leading-[1.02] tracking-[-0.01em] text-ink"
                  : "font-display text-[44px] leading-[1.02] md:text-[68px] lg:text-[80px] lg:leading-[0.98] tracking-[-0.01em] text-ink"
              }
            >
              {headline || defaultHeadline}
            </h1>

            <p
              data-testid="hero-subheadline"
              className="mt-8 max-w-[58ch] text-[17px] md:text-[18px] leading-[1.7] text-gray-800"
            >
              {subhead || defaultSubhead}
            </p>

            {showPriorResultsDisclaimer && (
              <p
                data-testid="hero-prior-results-disclaimer"
                className="mt-4 max-w-[58ch] font-body text-[12px] leading-relaxed italic text-gray-600"
              >
                Prior results do not guarantee a similar outcome.
              </p>
            )}

            <div className="mt-10 flex flex-col sm:items-start gap-3">
              <a
                href="#consultation"
                data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-center bg-teal text-ink hover:bg-ink hover:text-paper transition-colors duration-300 px-10 py-5 min-w-[320px]"
              >
                <span className="font-mono text-[12px] md:text-[13px] tracking-[0.24em] uppercase font-semibold">
                  {primaryCtaLabel}
                </span>
              </a>
              <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-gray-600">
                {ctaSubNote}
              </span>
            </div>

            {/* Stat bar */}
            {statBar !== null && (
              <div className="mt-16 md:mt-20">
                <p
                  data-testid="hero-stat-heading"
                  className="font-mono text-[10px] md:text-[11px] tracking-[0.28em] uppercase text-gray-600 mb-6"
                >
                  {statHeading}
                </p>
                <div
                  data-testid="hero-stat-bar"
                  className="rule-top rule-bottom py-10 grid grid-cols-3 gap-3 sm:gap-4 md:gap-8"
                >
                  {(statBar || [
                    { num: "2", label: "Law Firms Fired" },
                    { num: "$400K+", label: "In Legal Fees" },
                    { num: "Equal", label: "Custody Won" },
                  ]).map((s, i) => (
                    <div
                      key={s.label}
                      data-testid={`hero-stat-${i}`}
                      className="flex flex-col items-start"
                    >
                      <span className="font-display text-[36px] sm:text-[44px] md:text-[60px] leading-none tracking-tight text-ink">
                        {s.num}
                      </span>
                      <span className="mt-3 font-mono text-[9px] sm:text-[10px] md:text-[11px] tracking-[0.2em] sm:tracking-[0.24em] uppercase text-gray-600">
                        {s.label}
                      </span>
                    </div>
                  ))}
                </div>
                <p
                  data-testid="hero-stat-context"
                  className="mt-6 font-body text-[14px] md:text-[15px] leading-[1.7] text-gray-700 max-w-[64ch]"
                >
                  {statContext}
                </p>
              </div>
            )}

            {showPriorResultsDisclaimer && (
              <p
                data-testid="hero-stat-disclaimer"
                className="mt-4 font-body text-[12px] leading-relaxed italic text-gray-600"
              >
                Prior results do not guarantee a similar outcome.
              </p>
            )}

            {/* Editorial pull quote — balances right column height */}
            <div
              data-testid="hero-pullquote"
              className="mt-16 md:mt-20 grid grid-cols-12 gap-6 items-start"
            >
              <div className="col-span-12 md:col-span-2 flex md:block">
                <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-gray-600">
                  {pullquoteLabel}
                </span>
              </div>
              <div className="col-span-12 md:col-span-10">
                <p className="font-display text-[26px] md:text-[34px] leading-[1.25] text-ink">
                  {pullquote || defaultPullquote}
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-x-10 gap-y-4">
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT — image header + form column */}
          <div className="lg:col-span-5 reveal lg:sticky lg:top-28">
            <div className="relative">
              {/* Small editorial image header (compact) */}
              <div className="relative aspect-[16/8] w-full overflow-hidden bg-ink/5 mb-0">
                <img
                  src={heroImageProp || heroImage}
                  alt="Editorial portrait."
                  className="w-full h-full object-cover grayscale-[40%]"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/40 via-ink/10 to-transparent" />
                <p className="absolute bottom-4 left-5 right-5 font-display italic text-paper text-lg md:text-xl leading-snug max-w-[30ch]">
                  &ldquo;
                  {imageCaption ||
                    "Every parent deserves an attorney who has stood where they stand."}
                  &rdquo;
                </p>
              </div>

              {/* Form sits directly below the image header */}
              <ConsultationForm
                variant="hero"
                source={source}
                headline={formHeadline}
                sub={formSub}
                eyebrow={formEyebrow}
                ctaLabel={formCtaLabel}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
